import { formatPrice } from '../utils/formatPrice';
import { useState, useEffect } from 'react';
import { usePageTitle } from '../hooks/usePageTitle';
import { Building, MapPin, Mail, Phone, CreditCard, User, FileText, Download, Save, AlertCircle, Check } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { t } from '../data/translations';
import { authApi } from '../api/auth';

export default function ResellerAccount() {
  usePageTitle("Mon compte — Lodiss");

  const { lang, currentUser, refreshUser } = useApp();
  const T = t[lang];

  const [form, setForm] = useState({
    email: currentUser?.email || '',
    phone: currentUser?.phone || '',
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await authApi.updateProfile({ phone: form.phone });
      await refreshUser();
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err: any) {
      setError(err.message || (lang === 'fr' ? 'Erreur lors de la sauvegarde' : 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  const documents = [
    {
      id: 'catalogue',
      name: lang === 'fr' ? 'Catalogue produits LODISS 2024' : 'LODISS Product Catalogue 2024',
      size: '12.5 MB',
      type: 'PDF',
    },
    {
      id: 'certifications',
      name: lang === 'fr' ? 'Certifications & Conformités' : 'Certifications & Compliance',
      size: '8.2 MB',
      type: 'PDF',
    },
    {
      id: 'brand-assets',
      name: lang === 'fr' ? 'Charte graphique & Logos' : 'Brand Guidelines & Logos',
      size: '25.8 MB',
      type: 'ZIP',
    },
  ];

  return (
    <main className="bg-gray-50 min-h-screen py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-2xl font-bold text-gray-950 mb-6">
          {lang === 'fr' ? 'Mon compte' : 'My account'}
        </h1>

        {/* Company Profile */}
        <div className="bg-white rounded-xl border border-gray-100 p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-950 mb-4 flex items-center gap-2">
            <Building size={20} className="text-lodiss-red" />
            {lang === 'fr' ? 'Informations entreprise' : 'Company information'}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-xs font-semibold text-gray-500 mb-1">
                {lang === 'fr' ? 'Raison sociale' : 'Company name'}
              </p>
              <p className="font-medium text-gray-900">{currentUser?.company || '-'}</p>
            </div>

            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-xs font-semibold text-gray-500 mb-1">
                {lang === 'fr' ? 'Numéro TVA (19%)' : 'Tax ID'}
              </p>
              <p className="font-medium text-gray-900">{currentUser?.tax_id || '-'}</p>
            </div>

            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-xs font-semibold text-gray-500 mb-1">
                {lang === 'fr' ? 'Ville' : 'City'}
              </p>
              <p className="font-medium text-gray-900">{currentUser?.city || '-'}</p>
            </div>

            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-xs font-semibold text-gray-500 mb-1">
                {lang === 'fr' ? 'Conditions de paiement' : 'Payment terms'}
              </p>
              <p className="font-medium text-gray-900">{currentUser?.payment_terms || '30 days'}</p>
            </div>

            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-xs font-semibold text-gray-500 mb-1">
                {lang === 'fr' ? 'Volume mensuel' : 'Monthly volume'}
              </p>
              <p className="font-medium text-gray-900">{currentUser?.monthly_volume_tier || '-'}</p>
            </div>

            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-xs font-semibold text-gray-500 mb-1">
                {lang === 'fr' ? 'Limite de crédit' : 'Credit limit'}
              </p>
              <p className="font-medium text-gray-900">
                {currentUser?.credit_limit ? formatPrice(currentUser.credit_limit) : '-'}
              </p>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="bg-white rounded-xl border border-gray-100 p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-950 mb-4 flex items-center gap-2">
            <Mail size={20} className="text-lodiss-red" />
            {lang === 'fr' ? 'Informations de contact' : 'Contact information'}
          </h2>

          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-100 rounded-lg flex items-start gap-3">
              <AlertCircle size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {saved && (
            <div className="mb-4 p-4 bg-emerald-50 border border-emerald-100 rounded-lg flex items-start gap-3">
              <Check size={18} className="text-emerald-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-emerald-700">
                {lang === 'fr' ? 'Modifications enregistrées' : 'Changes saved'}
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                {lang === 'fr' ? 'Nom complet' : 'Full name'}
              </label>
              <input
                type="text"
                value={currentUser?.full_name || ''}
                disabled
                className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg bg-gray-50 text-gray-500"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                Email
              </label>
              <input
                type="email"
                value={form.email}
                disabled
                className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg bg-gray-50 text-gray-500"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                {lang === 'fr' ? 'Téléphone' : 'Phone'}
              </label>
              <input
                type="tel"
                value={form.phone}
                onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-lodiss-red focus:ring-1 focus:ring-lodiss-red/20"
              />
            </div>
          </div>

          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-lodiss-red hover:bg-lodiss-dark text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
          >
            {saving ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Save size={16} />
            )}
            {lang === 'fr' ? 'Enregistrer' : 'Save'}
          </button>
        </div>

        {/* Account Manager */}
        <div className="bg-white rounded-xl border border-gray-100 p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-950 mb-4 flex items-center gap-2">
            <User size={20} className="text-lodiss-red" />
            {lang === 'fr' ? 'Votre contact LODISS' : 'Your LODISS contact'}
          </h2>

          {currentUser?.account_manager_name ? (
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center">
                <User size={32} className="text-gray-500" />
              </div>
              <div>
                <p className="font-semibold text-gray-900">{currentUser.account_manager_name}</p>
                <p className="text-sm text-gray-500">{currentUser.account_manager_email}</p>
                <p className="text-sm text-gray-500">{currentUser.account_manager_phone}</p>
              </div>
            </div>
          ) : (
            <p className="text-gray-500">
              {lang === 'fr' 
                ? 'Aucun contact assigné pour le moment. Votre demande est en cours de traitement.'
                : 'No contact assigned yet. Your request is being processed.'}
            </p>
          )}
        </div>

        {/* Downloadable Documents */}
        <div className="bg-white rounded-xl border border-gray-100 p-6">
          <h2 className="text-lg font-bold text-gray-950 mb-4 flex items-center gap-2">
            <FileText size={20} className="text-lodiss-red" />
            {lang === 'fr' ? 'Documents à télécharger' : 'Downloadable documents'}
          </h2>

          <div className="space-y-3">
            {documents.map(doc => (
              <div key={doc.id} className="flex items-center justify-between p-4 border border-gray-100 rounded-lg hover:border-gray-200 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                    <FileText size={20} className="text-gray-500" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{doc.name}</p>
                    <p className="text-xs text-gray-500">{doc.type} • {doc.size}</p>
                  </div>
                </div>
                <button className="p-2 text-gray-400 hover:text-lodiss-red transition-colors">
                  <Download size={18} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </main>
  );
}
